import { endOf, startOf } from "@sembark-travel/datetime-utils"
import {
	Table,
	TableDataCell,
	TableHeaderDataCell,
	Text,
} from "@sembark-travel/ui-base"
import { Link } from "@sembark-travel/ui-router"
import { authLogsSearchFor } from "../../AuthLogs"
import { generatePath } from "../../router-utils"
import type { TTenantMonthlyUserMovement } from "./store"

/**
 * Users of tenants over a month, as in Monthly Accounts: at start and end,
 * added and deleted by part of the month, short-lived accounts and logins
 * from outside India. The Tenant and action columns show only when their
 * render functions are given (the report lists tenants; a tenant's own page
 * shows its single row).
 */
export function MonthlyAccountsTable<Row extends TTenantMonthlyUserMovement>({
	rows,
	month,
	renderTenant,
	renderActions,
}: {
	rows: Array<Row>
	month: Date
	renderTenant?: (row: Row) => React.ReactNode
	renderActions?: (row: Row) => React.ReactNode
}) {
	return (
		<Table bordered responsive striped>
			<thead>
				<tr>
					{renderTenant ? (
						<TableHeaderDataCell rowSpan={2}>Tenant</TableHeaderDataCell>
					) : null}
					<TableHeaderDataCell rowSpan={2} textAlign="right">
						Users at Start
					</TableHeaderDataCell>
					<TableHeaderDataCell colSpan={4} textAlign="center">
						Added
					</TableHeaderDataCell>
					<TableHeaderDataCell colSpan={4} textAlign="center">
						Deleted
					</TableHeaderDataCell>
					<TableHeaderDataCell rowSpan={2} textAlign="right">
						Users at End
					</TableHeaderDataCell>
					<TableHeaderDataCell
						rowSpan={2}
						textAlign="right"
						title="Users present for 15 days or fewer of the month (joined late or removed early)"
					>
						≤ 15 Days
					</TableHeaderDataCell>
					<TableHeaderDataCell colSpan={2} textAlign="center">
						Outside India
					</TableHeaderDataCell>
					{renderActions ? <TableHeaderDataCell rowSpan={2} /> : null}
				</tr>
				<tr>
					{MONTH_PART_HEADERS.map(([label, title], index, arr) => (
						<TableHeaderDataCell
							key={index}
							textAlign="right"
							title={title}
							bgColor="success"
							borderLeftWidth={index === 0 ? "1" : undefined}
							borderRightWidth={arr.length === index + 1 ? "1" : undefined}
						>
							{label}
						</TableHeaderDataCell>
					))}
					{MONTH_PART_HEADERS.map(([label, title], index, arr) => (
						<TableHeaderDataCell
							key={index}
							textAlign="right"
							title={title}
							bgColor="danger"
							borderLeftWidth={index === 0 ? "1" : undefined}
							borderRightWidth={arr.length === index + 1 ? "1" : undefined}
						>
							{label}
						</TableHeaderDataCell>
					))}
					<TableHeaderDataCell textAlign="right" borderLeftWidth="1">
						Logins
					</TableHeaderDataCell>
					<TableHeaderDataCell textAlign="right" borderRightWidth="1">
						Users
					</TableHeaderDataCell>
				</tr>
			</thead>
			<tbody>
				{rows.map((row) => (
					<tr key={row.tenant.id}>
						{renderTenant ? (
							<TableDataCell>{renderTenant(row)}</TableDataCell>
						) : null}
						<NumberCell value={row.users_at_start} />
						<NumberCell value={row.new_users} strong borderLeftWidth="1" />
						<NumberCell value={row.new_users_before_10th} />
						<NumberCell value={row.new_users_10th_to_19th} />
						<NumberCell
							value={row.new_users_from_20th}
							highlight
							borderRightWidth="1"
						/>
						<NumberCell value={row.deletions} strong borderLeftWidth="1" />
						<NumberCell value={row.deletions_before_10th} highlight />
						<NumberCell value={row.deletions_10th_to_19th} />
						<NumberCell value={row.deletions_from_20th} borderRightWidth="1" />
						<NumberCell value={row.users_at_end} strong />
						<NumberCell value={row.short_lived_users} highlight />
						<TableDataCell textAlign="right" borderLeftWidth="1">
							{row.foreign_logins ? (
								<Link
									to={
										generatePath("/admin/auth-logs") +
										authLogsSearchFor({
											tenants: [row.tenant],
											event: "login",
											outside_india: true,
											created_after: startOf(month, "month"),
											created_before: endOf(month, "month"),
										})
									}
									color="warning"
									fontWeight="semibold"
									title="See these logins"
								>
									{row.foreign_logins}
								</Link>
							) : (
								<Text color="muted">0</Text>
							)}
						</TableDataCell>
						<NumberCell
							value={row.foreign_login_users}
							highlight
							borderRightWidth="1"
						/>
						{renderActions ? (
							<TableDataCell textAlign="right">
								{renderActions(row)}
							</TableDataCell>
						) : null}
					</tr>
				))}
			</tbody>
		</Table>
	)
}

/**
 * Sub-columns of Added and Deleted: the parts of the month.
 */
const MONTH_PART_HEADERS: Array<[string, string]> = [
	["Total", "All in the month"],
	["< 10", "Before the 10th"],
	["10–19", "From the 10th to the 19th"],
	["≥ 20", "From the 20th to the month end"],
]

/**
 * A right-aligned count; 0 is muted, `highlight` marks non-zero counts.
 */
function NumberCell({
	value,
	strong,
	highlight,
	...props
}: {
	value: number
	strong?: boolean
	highlight?: boolean
} & Pick<
	React.ComponentProps<typeof TableDataCell>,
	"borderLeftWidth" | "borderRightWidth"
>) {
	return (
		<TableDataCell textAlign="right" {...props}>
			<Text
				color={!value ? "muted" : highlight ? "warning" : undefined}
				fontWeight={strong || (highlight && value) ? "semibold" : undefined}
			>
				{value}
			</Text>
		</TableDataCell>
	)
}
